import { useState } from 'react';
import { Card, CardContent, SimpleTabBar, PostLink } from '../../ui';
import { cn } from '../../../utils/cn';

/**
 * The three posts worth looking at, and the room's mood in a line.
 *
 * This was four cards in a four-column grid. Two things were wrong with it.
 * A CSS grid row stretches every cell to the tallest, so one long mood
 * paragraph inflated all four cards and left three of them mostly white -
 * the block's height was set by its wordiest element rather than by its
 * content. And three posts side by side gave each a narrow column, so post
 * text wrapped into a tall stack and read worse the more there was of it.
 *
 * Now: the mood is one clamped line above, and the three posts are one
 * fixed-height row behind tabs. The counts stay on the tabs, so switching is
 * not needed to compare them - the tab answers "which post" and the row
 * answers "what did it say". A post reads along a line instead of down a
 * column, and its length can no longer change the layout.
 */

function Delta({ now, before }) {
	if (before == null || now == null) return null;
	const d = now - before;
	if (d === 0) return <span className="text-xs text-muted-foreground">no change</span>;
	return <span className={`text-xs font-medium ${d > 0 ? 'text-green-700' : 'text-red-700'}`}>{d > 0 ? '+' : ''}{d}</span>;
}

// Stance when the tile has it (a run labelled with the parent post), polarity
// otherwise (an older run not yet relabelled). Polarity alone could not tell an
// ally's anger from an attacker's, which is why the tiles moved to stance.
const forOf = (t) => (t.stance ? t.stance.for : t.polarity.positive);
const againstOf = (t) => (t.stance ? t.stance.against : t.polarity.negative);
function share(t) {
	if (!t) return null;
	const total = t.stance ? (t.stance.for || 0) + (t.stance.against || 0) + (t.stance.none || 0) : t.replyCount;
	return total ? Math.round((forOf(t) / total) * 100) : null;
}

export function MoodTiles({ mood, tiles = {}, previousTiles, onOpen, className }) {
	const items = [
		// "Most divisive" has no meaningful before/after number: showing the
		// with-you share's delta there implied more divisiveness is "better"
		// (an unlabelled green "+3" on a tile whose stat is 14-for/14-against).
		// There's no good single-number delta for divisiveness, so this tile
		// simply shows none.
		{ key: 'loudest', label: 'Loudest post', t: tiles.loudest, stat: (t) => `${t.replyCount} replies`, count: (t) => t.replyCount, deltaNow: (t) => t.replyCount, deltaBefore: (t) => t.replyCount },
		{ key: 'mostLoved', label: 'Most supported', t: tiles.mostLoved, stat: (t) => share(t) === null ? 'Standing unclear' : `${share(t)}% support`, count: share, deltaNow: share, deltaBefore: share },
		{ key: 'mostDivisive', label: 'Most divisive', t: tiles.mostDivisive, stat: (t) => `${forOf(t)} with · ${againstOf(t)} against`, count: (t) => t.replyCount, deltaNow: null, deltaBefore: null },
	];

	// Open on the first tile that actually has a post, so a run where the
	// loudest post is missing does not land the user on an empty tab.
	const [active, setActive] = useState(() => (items.find((i) => i.t) || items[0]).key);
	const current = items.find((i) => i.key === active) || items[0];
	const { t, label, stat, deltaNow, deltaBefore } = current;

	const tabs = items.map((i) => ({
		id: i.key,
		label: i.label,
		// The number rides on the tab so all three are comparable without
		// switching; a tab with no post says so rather than showing a zero.
		count: i.t ? i.count(i.t) : undefined,
	}));

	return (
		<Card className={className}>
			<CardContent className="p-4 sm:p-5">
				{/* The mood, clamped. It used to be a card of its own whose length
				    set the height of the whole row. */}
				<p className="line-clamp-2 text-sm leading-relaxed text-foreground">
					{mood?.summary || 'Not enough replies came in to sum up the mood this time.'}
				</p>

				<div className="mt-3 border-t border-border pt-1">
					<SimpleTabBar tabs={tabs} activeTab={active} onTabChange={setActive} showCounts />
				</div>

				{/* Fixed height whatever the post says: one line of text, the stat
				    pinned right. A longer post truncates rather than growing. */}
				<button
					type="button"
					disabled={!t}
					onClick={() => t && onOpen?.({ parentPostId: t.postId, url: t.url, title: label, text: t.text })}
					className={cn(
						// Wraps on a narrow screen: side by side, the stat is wide
						// enough to squeeze the post text to nothing, which is how
						// the post vanished entirely on a phone.
						'mt-3 flex w-full flex-col items-start gap-1 rounded-lg border border-border px-3 py-2.5 text-left transition-colors sm:flex-row sm:items-center sm:gap-4',
						t ? 'cursor-pointer hover:border-border hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500' : 'cursor-default'
					)}
				>
					{t ? (
						<>
							<span className="w-full min-w-0 text-sm text-foreground sm:flex-1">
								<PostLink url={t.url} truncate>{t.text || t.postId}</PostLink>
							</span>
							<span className="flex shrink-0 items-center gap-2">
								<span className="text-sm font-medium tabular-nums text-foreground">{stat(t)}</span>
								{deltaNow && <Delta now={deltaNow(t)} before={previousTiles?.[active] ? deltaBefore(previousTiles[active]) : null} />}
								<span className="text-xs font-medium text-brand-600">Read replies</span>
							</span>
						</>
					) : (
						<span className="text-sm text-muted-foreground">Needs at least 10 replies on a post.</span>
					)}
				</button>
				{active === 'mostLoved' && t?.unclear > 0 && <p className="mt-1 text-xs text-muted-foreground">Excludes {t.unclear} unclear {t.unclear === 1 ? 'reply' : 'replies'}</p>}
			</CardContent>
		</Card>
	);
}
